import { useState, useEffect } from 'react';
import { doc, getDoc, collection, getDocs, DocumentData } from 'firebase/firestore';
import { db } from '@/config/firebase'; // Adjust path as needed
import { parseNumericValue } from '@/services/pmt2Utils'; // Adjust path as needed

interface LifetimeResultsData {
    asinData: DocumentData | null;
    companyLifetimeRevenue: number | null;
}

interface UsePmt3LifetimeResultsDataHook extends LifetimeResultsData {
    isLoading: boolean;
    error: Error | null;
    refetch: () => void; // Optional refetch capability
}

const LIFETIME_RESULTS_COLLECTION = 'pmt2_lifetime_results';
const ENTRIES_SUBCOLLECTION = 'entries';

/**
 * Hook to fetch Lifetime Results data for a specific ASIN and the total for the company.
 */
export const usePmt3LifetimeResultsData = (
    companyName: string | null | undefined,
    selectedAsin: string | null | undefined
): UsePmt3LifetimeResultsDataHook => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);
    const [asinData, setAsinData] = useState<DocumentData | null>(null);
    const [companyLifetimeRevenue, setCompanyLifetimeRevenue] = useState<number | null>(null);
    const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

    const refetch = () => setRefreshTrigger(prev => prev + 1);

    useEffect(() => {
        const loadData = async () => {
            if (!companyName) {
                setAsinData(null);
                setCompanyLifetimeRevenue(null);
                setIsLoading(false);
                setError(null);
                return;
            }

            setIsLoading(true);
            setError(null);
            setAsinData(null); // Clear previous data
            setCompanyLifetimeRevenue(null); // Clear previous data

            try {
                const companyEntriesRef = collection(db, LIFETIME_RESULTS_COLLECTION, companyName, ENTRIES_SUBCOLLECTION);

                // Fetch all entries for the company to calculate total revenue
                const allEntriesQuery = getDocs(companyEntriesRef);

                // Fetch specific ASIN data if selectedAsin is provided
                const asinDataQuery = selectedAsin
                    ? getDoc(doc(db, LIFETIME_RESULTS_COLLECTION, companyName, ENTRIES_SUBCOLLECTION, selectedAsin))
                    : Promise.resolve(null); // Resolve immediately if no ASIN selected

                // Run queries concurrently
                const [allEntriesSnapshot, asinDocSnapshot] = await Promise.all([
                    allEntriesQuery,
                    asinDataQuery
                ]);

                // Calculate total company revenue
                let totalRevenue = 0;
                allEntriesSnapshot.forEach(docSnap => {
                    const revenue = parseNumericValue(docSnap.data()?.lifetimeRevenue);
                    if (revenue !== null) {
                        totalRevenue += revenue;
                    }
                });
                setCompanyLifetimeRevenue(totalRevenue);

                // Set specific ASIN data
                if (asinDocSnapshot && asinDocSnapshot.exists()) {
                    setAsinData({ id: asinDocSnapshot.id, ...asinDocSnapshot.data() });
                } else {
                    setAsinData(null); // Ensure it's null if doc doesn't exist or no ASIN selected
                    if (selectedAsin) {
                         console.warn(`[LifetimeResultsHook] No lifetime data found for ASIN: ${selectedAsin}`);
                    }
                }

            } catch (err) {
                console.error("Error fetching Lifetime Results data:", err);
                setError(err instanceof Error ? err : new Error('Failed to fetch Lifetime Results data'));
                setAsinData(null);
                setCompanyLifetimeRevenue(null);
            } finally {
                setIsLoading(false);
            }
        };

        loadData();

    }, [companyName, selectedAsin, refreshTrigger]); // Re-run effect if company, ASIN, or trigger changes

    return { isLoading, error, asinData, companyLifetimeRevenue, refetch };
};